import { checkFlightLegCount, FLIGHT_LEG_BOUNDS } from '@massilia/contracts';
import { z } from 'zod';
import { airportOptionSchema, checkPassengerCount, clientFirstNameSchema, clientLastNameSchema, contactEmailSchema, contactPhoneSchema, DEFAULT_OPTION, flexibilityDaysSchema, futureDateSchema, newPassengerSchema, PASSENGER_COUNT_ERROR, travelClassSchema, } from './base-schemas';
// =====================================================================
// FLIGHT ESTIMATE SCHEMAS
// Validation schemas for flight estimate forms with French error messages
// =====================================================================
export const flightTypes = {
    MULTI_DESTINATION: 'MULTI_DESTINATION',
    ONE_WAY: 'ONE_WAY',
    ROUND_TRIP: 'ROUND_TRIP',
};
const flightTypeSchema = z.enum(['MULTI_DESTINATION', 'ONE_WAY', 'ROUND_TRIP']);
// ---- Company Preference Schema ----
const companyPreferenceSchema = z
    .object({
    companyName: z.object({ label: z.string(), value: z.string() }),
    hasPreference: z.boolean(),
})
    .refine((data) => !data.hasPreference || data.companyName.value.trim().length > 0, {
    message: 'Nom de la compagnie requis si préférence indiquée',
    path: ['companyName'],
});
// ---- Flight Leg Schema (shared building block) ----
const flightLegSchema = z.object({
    date: futureDateSchema,
    departure: airportOptionSchema,
    destination: airportOptionSchema,
    flexibilityAfter: flexibilityDaysSchema,
    flexibilityBefore: flexibilityDaysSchema,
});
// ---- Flight Estimate Form Base Schema ----
const flightEstimateFormBaseFields = {
    clientFirstName: clientFirstNameSchema,
    clientLastName: clientLastNameSchema,
    contactEmail: contactEmailSchema,
    companyPreference: companyPreferenceSchema,
    complementaryInformation: z.string(),
    contactPhoneNumber: contactPhoneSchema,
    existingPassengerIds: z.array(z.string()).optional(),
    maxStayDuration: z.coerce.number().int().min(0).optional(),
    newPassengers: z.array(newPassengerSchema).optional(),
    particularities: z.string(),
    travelClass: travelClassSchema,
    type: flightTypeSchema,
};
// ---- Discriminated Union Schema for Type-Safe Validation ----
// Each schema includes estimateType: 'FLIGHT' for the unified discriminated union
export const oneWayFlightEstimateSchema = z
    .object({
    ...flightEstimateFormBaseFields,
    estimateType: z.literal('FLIGHT'),
    flights: z.array(flightLegSchema).superRefine(addLegCountIssue('ONE_WAY')),
    type: z.literal('ONE_WAY'),
})
    .refine(checkPassengerCount, PASSENGER_COUNT_ERROR);
export const roundTripFlightEstimateSchema = z
    .object({
    ...flightEstimateFormBaseFields,
    estimateType: z.literal('FLIGHT'),
    flights: z
        .array(flightLegSchema)
        .superRefine(addLegCountIssue('ROUND_TRIP'))
        .refine((flights) => {
        // Le comptage est déjà fait par addLegCountIssue : si ce n'est pas 2, on passe.
        if (flights.length !== 2)
            return true;
        const outbound = new Date(flights[0].date);
        const returnFlight = new Date(flights[1].date);
        return returnFlight > outbound;
    }, {
        message: 'La date de retour doit être après la date de départ',
        path: [1, 'date'],
    }),
    type: z.literal('ROUND_TRIP'),
})
    .refine(checkPassengerCount, PASSENGER_COUNT_ERROR);
export const multiDestinationFlightEstimateSchema = z
    .object({
    ...flightEstimateFormBaseFields,
    estimateType: z.literal('FLIGHT'),
    flights: z.array(flightLegSchema).superRefine(addLegCountIssue('MULTI_DESTINATION')),
    type: z.literal('MULTI_DESTINATION'),
})
    .refine(checkPassengerCount, PASSENGER_COUNT_ERROR);
/**
 * Ramène une liste de tronçons au format qu'impose le type de voyage, pour qu'une demande
 * incohérente reste MODIFIABLE.
 *
 * Sans ça, un aller-retour enregistré avec un seul tronçon rouvrait un formulaire que rien
 * ne pouvait plus soumettre : la validation réclamait deux vols, et le bouton « Ajouter un
 * vol » n'existe que pour les multi-destinations. L'agent ne pouvait plus rien changer,
 * pas même un passager.
 *
 * Le tronçon retour ajouté reprend le trajet de l'aller en sens inverse — ce que fait déjà
 * la saisie d'un aller-retour — mais SANS date : elle n'existe nulle part, c'est à l'agent
 * de la renseigner, et le champ la réclame explicitement.
 */
export function normalizeFlightLegsForType(flights, type) {
    const { max, min } = FLIGHT_LEG_BOUNDS[type];
    const normalized = flights.slice(0, max);
    while (normalized.length < min) {
        const outbound = normalized[0];
        normalized.push(outbound
            ? {
                ...EMPTY_FLIGHT_LEG,
                departure: outbound.destination,
                destination: outbound.departure,
            }
            : { ...EMPTY_FLIGHT_LEG });
    }
    return normalized;
}
/**
 * Même règle de comptage que le contrat d'API (`checkFlightLegCount`) : le formulaire et
 * le serveur doivent accepter exactement les mêmes demandes.
 */
function addLegCountIssue(type) {
    return (flights, ctx) => {
        const message = checkFlightLegCount(type, flights.length);
        if (message)
            ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    };
}
// ---- Default Values ----
export const EMPTY_FLIGHT_LEG = {
    date: '',
    departure: DEFAULT_OPTION,
    destination: DEFAULT_OPTION,
    flexibilityAfter: 0,
    flexibilityBefore: 0,
};
